import React from 'react';
import { AlertTriangle } from 'lucide-react';
import DiscountFields from './DiscountFields';
import { money } from './financeFormat';
import { offerTermError, type OfferTermInput } from '../../services/billingApi';
import type { OfferAmounts } from '@platform/shared-types';

/**
 * THE OFFER CONTROL — one editor for what a deal changes about one billable
 * line, whether that line is a software package or a device finance option.
 *
 * It replaces three: a package's discount, a package's introductory price, and
 * a device's terms drawer. They asked the same four questions in three
 * different layouts, so an operator pricing a deal had to learn the answer to
 * "how do I discount this" separately for software and for hardware — and the
 * capability gaps between them (no percentage promotion on software, no fixed
 * price on hardware) were invisible until someone tried.
 *
 * The four questions, in the order an operator answers them:
 *
 *   1. What does it settle at?      the discount row
 *   2. Where does that come off?    monthly / due today  (hidden when only one applies)
 *   3. Is there an opening period?  the promotion row
 *   4. How many units?              the covers row       (devices only)
 *
 * Every number shown as an OUTCOME comes from `offerAmounts` — the same pure
 * function the server bills from. This component computes no prices.
 */

const LABEL = 'block text-[10px] uppercase tracking-[.09em] text-stone-500 font-bold mb-1.5';
const INPUT =
  'px-2.5 py-2 bg-white border border-stone-300 rounded-lg text-stone-800 text-sm '
  + 'outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[40px] tabular-nums';

/** A pill switch. Custom, never a native select — platform UI rule. */
const Choice = <T extends string>({ value, onChange, options, disabled }: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
}) => (
  <div className={`inline-flex rounded-lg border border-stone-300 bg-white overflow-hidden ${disabled ? 'opacity-50' : ''}`}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        disabled={disabled}
        onClick={() => onChange(opt.value)}
        className={`px-3 py-2 text-xs font-semibold min-h-[40px] border-r border-stone-200 last:border-r-0 transition ${
          value === opt.value ? 'bg-rose-700 text-white' : 'text-stone-600 hover:bg-stone-50'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const Toggle: React.FC<{ on: boolean; onClick: () => void; label: React.ReactNode }> = ({ on, onClick, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none min-h-[32px]">
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      className={`relative w-[34px] h-[19px] rounded-full transition shrink-0 ${on ? 'bg-rose-700' : 'bg-stone-300'}`}
    >
      <span className={`absolute top-[2px] w-[15px] h-[15px] rounded-full bg-white transition-all ${on ? 'right-[2px]' : 'left-[2px]'}`} />
    </button>
    {label}
  </label>
);

export interface OfferTermFieldsProps {
  value: OfferTermInput;
  /** Receives a PARTIAL patch; the caller merges it into its own form state. */
  onChange: (patch: Partial<OfferTermInput>) => void;
  /**
   * The priced outcome, computed by the caller through `offerAmounts`. Never
   * recomputed here — a control that did its own arithmetic would be a second
   * implementation of the one thing that must have exactly one.
   */
  amounts?: OfferAmounts;
  /** False for an outright purchase: there is no monthly amount to discount. */
  hasMonthly: boolean;
  /** True when this line can carry an up-front amount — a device, not a package. */
  hasOneTime: boolean;
  /** True for a device: quantity is a real thing. A package is always one. */
  hasQuantity: boolean;
  /** A device's term, for "× 24 months" in the outcome line. */
  termMonths?: number;
  /** Wrapper classes. */
  className?: string;
}

export const OfferTermFields: React.FC<OfferTermFieldsProps> = ({
  value, onChange, amounts, hasMonthly, hasOneTime, hasQuantity, termMonths, className = '',
}) => {
  const error = offerTermError(value, { hasMonthly, hasOneTime });
  const hasDiscount = value.discount.mode !== 'none';

  /**
   * ONE OR THE OTHER — a discount OR a limited-time price, never both (product
   * decision, 2026-09-04).
   *
   * Enforced HERE and nowhere else. `DealOfferTermSchema` still permits both, so
   * a deal saved before this rule keeps pricing exactly as it always did and no
   * stored data has to be migrated or re-validated; what changes is that the
   * combination can no longer be BUILT.
   *
   * Each control CLEARS the other rather than disabling it. Disabling both would
   * trap an operator on a legacy deal that carries both with no way to remove
   * either, and disabling one is what makes an operator wonder whether the app
   * is broken. Clearing is stated in the copy under each control, so nothing
   * disappears silently.
   */
  const both = hasDiscount && value.promoEnabled;

  const pickDiscount = (patch: Partial<OfferTermInput['discount']>) => {
    const next = { ...value.discount, ...patch };
    // Setting a real discount retires the limited-time price.
    if (next.mode !== 'none' && value.promoEnabled) {
      onChange({ discount: next, promoEnabled: false, promoValue: '' });
      return;
    }
    onChange({ discount: next });
  };

  const togglePromo = () => {
    if (value.promoEnabled) {
      onChange({ promoEnabled: false });
      return;
    }
    // Turning the limited-time price on retires the discount.
    onChange({
      promoEnabled: true,
      ...(hasDiscount ? { discount: { mode: 'none' as const, value: '', reason: value.discount.reason } } : {}),
    });
  };
  // With only one amount to touch there is no choice to offer — the label
  // states which one rather than showing a control that can only be refused.
  const targetLocked = !hasMonthly || !hasOneTime;
  const lockedTarget = hasMonthly ? 'monthly' : 'one_time';
  const target = targetLocked ? lockedTarget : value.takenFrom;

  return (
    <div className={`relative ${className}`}>
      {/* 1 + 2 — what it settles at, and which amount that comes off. */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div>
          <span className={LABEL}>Discount</span>
          <DiscountFields
            label=""
            variant="segmented"
            tone="light"
            value={value.discount}
            onChange={pickDiscount}
            showReason={false}
            rowClassName="flex items-center gap-2 flex-wrap"
          />
          {value.promoEnabled && !hasDiscount && (
            <p className="mt-1 text-[10px] text-stone-400">
              Setting one removes the limited-time price below.
            </p>
          )}
        </div>

        {!targetLocked && (
          <div>
            <span className={LABEL}>Taken from</span>
            <Choice
              value={value.takenFrom}
              disabled={!hasDiscount}
              onChange={(takenFrom) => onChange({ takenFrom })}
              options={[
                { value: 'monthly' as const, label: 'Monthly' },
                { value: 'one_time' as const, label: 'Due today' },
              ]}
            />
          </div>
        )}

        {hasOneTime && (
          <div>
            <span className={LABEL}>Down payment</span>
            <div className="flex items-center gap-1.5">
              <span className="text-stone-400 text-sm">$</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="default"
                className={`${INPUT} w-24`}
                value={value.downPaymentDollars}
                onChange={(e) => onChange({ downPaymentDollars: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      {/* 3 — the opening period. */}
      <div className="mt-2.5 pt-2.5 border-t border-dashed border-stone-200">
        <Toggle
          on={value.promoEnabled}
          onClick={togglePromo}
          label={
            <span className="text-[11px] font-bold uppercase tracking-[.07em] text-stone-600">
              Limited-time price
              {!value.promoEnabled && (
                <span className="ml-1.5 text-stone-400 normal-case font-normal tracking-normal">
                  {hasDiscount
                    ? 'charge less for the first months — turning this on removes the discount above'
                    : 'charge less for the first months, then the price above'}
                </span>
              )}
            </span>
          }
        />

        {/* A deal saved BEFORE the one-or-the-other rule can still carry both.
            It keeps pricing exactly as it always has — nothing is cleared on
            load — but the operator is told, because the next touch of either
            control will drop the other. */}
        {both && (
          <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-800">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            This offer carries a discount <em>and</em> a limited-time price — a combination newer deals cannot be
            built with. It still prices as saved. Changing either one will remove the other.
          </p>
        )}

        {value.promoEnabled && (
          <div className="flex flex-wrap items-end gap-x-3 gap-y-2 mt-2">
            <div>
              <span className={LABEL}>Charge</span>
              <Choice
                value={value.promoMode}
                onChange={(promoMode) => onChange({ promoMode })}
                options={[
                  { value: 'fixed' as const, label: '$ price' },
                  { value: 'percent' as const, label: '% off' },
                ]}
              />
            </div>
            <div>
              <span className={LABEL}>{value.promoMode === 'fixed' ? 'Amount' : 'Percent'}</span>
              <div className="flex items-center gap-1.5">
                {value.promoMode === 'fixed' && <span className="text-stone-400 text-sm">$</span>}
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={value.promoMode === 'fixed' ? '0.00' : '50'}
                  className={`${INPUT} w-24`}
                  value={value.promoValue}
                  onChange={(e) => onChange({ promoValue: e.target.value })}
                />
                {value.promoMode === 'percent' && <span className="text-stone-400 text-sm">%</span>}
              </div>
            </div>
            <span className="text-xs text-stone-500 pb-3">for the first</span>
            <div>
              <span className={LABEL}>Months</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                className={`${INPUT} w-20`}
                value={value.promoMonths}
                onChange={(e) => onChange({ promoMonths: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      {/* 4 — how many units. Devices only; a package is always one. */}
      {hasQuantity && (
        <div className="mt-2.5 pt-2.5 border-t border-dashed border-stone-200 flex flex-wrap items-end gap-x-4 gap-y-2">
          <div>
            <span className={LABEL}>Covers</span>
            <Choice
              value={value.covers}
              onChange={(covers) => onChange({ covers })}
              options={[
                { value: 'each' as const, label: 'Each device' },
                { value: 'first_n' as const, label: 'First N only' },
              ]}
            />
          </div>
          {value.covers === 'first_n' && (
            <div>
              <span className={LABEL}>Devices covered</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                className={`${INPUT} w-24`}
                value={value.coveredUnits}
                onChange={(e) => onChange({ coveredUnits: e.target.value })}
              />
            </div>
          )}

          {/* HOW MANY LOCATIONS — a DIFFERENT question from "how many units".
              "The first two terminals at each of your first two stores" needs
              both, so they are two fields rather than one.

              The field is in `DealOfferTermSchema`, `deviceDealTiers` prices
              from it and billing-service bills from it — but nothing could set
              it until now, so an offer could never be limited to a tenant's
              first stores however the deal was written. */}
          <div>
            <span className={LABEL}>Locations</span>
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              placeholder="all"
              className={`${INPUT} w-24`}
              value={value.coveredLocations}
              onChange={(e) => onChange({ coveredLocations: e.target.value })}
            />
          </div>
          {hasOneTime && (
            <Toggle
              on={value.showOneTimeInPreview}
              onClick={() => onChange({ showOneTimeInPreview: !value.showOneTimeInPreview })}
              label={
                <span className="text-[11px] text-stone-600">
                  Show &ldquo;due today&rdquo; to the customer
                  {!value.showOneTimeInPreview && (
                    <span className="text-stone-400"> — hidden from the preview, still billed</span>
                  )}
                </span>
              }
            />
          )}
        </div>
      )}

      {/* Internal only — never reaches a customer. */}
      <div className="mt-2.5">
        <span className={LABEL}>
          Why <span className="normal-case tracking-normal font-medium text-stone-400">(internal)</span>
        </span>
        <input
          type="text"
          className={`${INPUT} w-full max-w-md`}
          placeholder="e.g. 3-store commitment, ISO partner rate"
          value={value.reason}
          onChange={(e) => onChange({ reason: e.target.value })}
        />
      </div>

      {/* THE OUTCOME, in the customer's terms. */}
      {error ? (
        <p className="mt-2.5 flex items-start gap-1.5 text-xs text-amber-800">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {error}
        </p>
      ) : amounts && amounts.priced && (
        <p className="mt-2.5 pt-2.5 border-t border-stone-200 text-xs text-stone-700 leading-relaxed">
          Customer sees{' '}
          {target === 'monthly' ? (
            <>
              {amounts.promoMonths ? (
                <>
                  <strong className="tabular-nums">
                    {amounts.promoMonthlyCents === 0 ? 'Free' : `${money(amounts.promoMonthlyCents)}/mo`}
                  </strong>
                  {' for '}{amounts.promoMonths} month{amounts.promoMonths === 1 ? '' : 's'}
                  {', then '}
                  <strong className="tabular-nums">{money(amounts.settledMonthlyCents)}/mo</strong>
                </>
              ) : (
                <strong className="tabular-nums">{money(amounts.settledMonthlyCents)}/mo</strong>
              )}
              {termMonths ? <> × {termMonths}</> : null}
              {amounts.listMonthlyCents > amounts.settledMonthlyCents && (
                <> — normally <span className="line-through tabular-nums text-stone-400">{money(amounts.listMonthlyCents)}</span></>
              )}
            </>
          ) : (
            <>
              <strong className="tabular-nums">{money(amounts.settledOneTimeCents)}</strong>
              {amounts.listOneTimeCents > amounts.settledOneTimeCents && (
                <> — was <span className="line-through tabular-nums text-stone-400">{money(amounts.listOneTimeCents)}</span></>
              )}
            </>
          )}
          {hasOneTime && target === 'monthly' && (
            <>
              {' · '}
              <strong className="tabular-nums">
                {amounts.settledOneTimeCents === 0 ? '$0 down' : `${money(amounts.settledOneTimeCents)} down`}
              </strong>
            </>
          )}
          {'. '}
          {/* THE SCOPE, in one sentence: how many units, and at how many
              locations. Both were settable and neither was ever read back to
              the operator, so an offer limited to two stores looked identical
              to one that reached every store. */}
          {hasQuantity && (() => {
            const units = value.covers === 'each'
              ? 'Applies to every one they add'
              : `Applies to the first ${value.coveredUnits || '?'} at a location; the rest at the standard price`;
            const locations = (value.coveredLocations || '').trim();
            const n = Number(locations);
            if (!locations || !Number.isInteger(n) || n < 1) return `${units}, at every location.`;
            return `${units} — but only at their first ${n === 1 ? 'location' : `${n} locations`}. Any location after that pays the standard price.`;
          })()}
        </p>
      )}
    </div>
  );
};

export default OfferTermFields;
